import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { Task, SubTask, type NewSubtaskFields } from "../types";
import {
  registerRecurringChangeConfirmHandler,
  type RecurringChangeTask,
} from "../lib/confirmRecurringDateChange";
import { type useDialog } from "../contexts/DialogContext";
import {
  buildTaskUpdatePayload,
  isNewerRevision,
  sameTaskContent,
} from "../lib/taskUpdatePayload";
import { useEditingTaskTags } from "./useEditingTaskTags";
import { saveNewSubtasks } from "../lib/saveNewSubtasks";
import { useSubtaskConversionHandlers } from "./useSubtaskConversionHandlers";
import { useDialogCycle, type DialogCycleFocus } from "./useDialogCycle";

// The mutations this hook writes through, supplied by its caller
export interface TaskEditingMutations {
  toggleTaskInDb: (id: string) => Promise<boolean>;
  updateTaskInDb: (
    id: string,
    updates: Partial<Task>,
    options?: { confirmedRecurringDateChange?: boolean },
  ) => Promise<boolean>;
  updateSubtaskInDb: (
    subtaskId: string,
    updates: {
      title?: string;
      completed?: boolean;
      order_position?: number;
      description?: string;
      priority?: number;
      scheduled_date?: string | null;
      scheduled_time?: string | null;
      deadline_date?: string | null;
      duration?: number | null;
      persistent?: boolean;
      working_hours_only?: boolean;
    },
  ) => Promise<boolean>;
  deleteTaskFromDb: (id: string) => Promise<boolean>;
  archiveTaskInDb: (id: string) => Promise<boolean>;
  addSubtaskToDb: (taskId: string, title: string, fields?: NewSubtaskFields) => Promise<SubTask | null>;
  // Removes the row and optimistically updates the tasks query cache, so the
  // open dialog reflects the removal immediately.
  deleteSubtaskFromDb: (id: string) => Promise<boolean>;
  // The batched form, for a save that removed several subtasks at once: one
  // DELETE and one cache invalidation instead of one of each per subtask.
  deleteSubtasksFromDb: (ids: string[]) => Promise<boolean>;
  moveSubtaskInDb: (
    subtaskId: string,
    sourceTaskId: string,
    targetTaskId: string,
  ) => Promise<boolean>;
  convertSubtaskToTaskInDb: (subtaskId: string) => Promise<Task | null>;
  convertTaskToSubtaskInDb: (
    taskId: string,
    targetTaskId: string,
  ) => Promise<boolean>;
  setTaskTags: (taskId: string, tagIds: string[]) => Promise<boolean>;
}

export interface UseTaskEditingArgs {
  tasks: Task[];
  taskId: string | undefined;
  isLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  navigate: any;
  pathname: string;
  dialog: ReturnType<typeof useDialog>;
  viewBeforeTaskOpen: React.MutableRefObject<string>;
  visibleTasks: Task[];
  clearSearchTerm?: () => void;
  /** The sub-task open in the dialog, when the route names one. */
  activeSubtaskId?: string | null;
  /**
   * Set when a saved Today filter is what the board is showing. The arrows
   * then step through the tasks and sub-tasks dated today.
   */
  navigationFocus?: DialogCycleFocus;
  mutations: TaskEditingMutations;
}

export interface UseTaskEditingResult {
  editingTask: Task | null;
  setEditingTask: React.Dispatch<React.SetStateAction<Task | null>>;
  originalEditingTask: Task | null;
  editingTaskTagIds: string[];
  setEditingTaskTagIds: React.Dispatch<React.SetStateAction<string[]>>;
  searchJumpedFromTaskId: string | null;
  setSearchJumpedFromTaskId: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  navigationSnapshotIds: string[];
  setNavigationSnapshotIds: React.Dispatch<React.SetStateAction<string[]>>;
  navigationTasks: Task[];
  /** True when the dialog arrows have another matching item to open. */
  canNavigate: boolean;
  showEditDialog: boolean;
  taskNotFound: boolean;
  convertingTaskId: string | null;
  setConvertingTaskId: React.Dispatch<React.SetStateAction<string | null>>;
  pendingRecurringChange: {
    task: RecurringChangeTask;
    resolve: (confirmed: boolean) => void;
  } | null;
  setPendingRecurringChange: React.Dispatch<
    React.SetStateAction<{
      task: RecurringChangeTask;
      resolve: (confirmed: boolean) => void;
    } | null>
  >;
  updateTask: () => Promise<void>;
  handleEditTask: (task: Task, subtaskId?: string) => void;
  handleCloseEditDialog: () => void;
  handleToggleCompleteFromDialog: (
    taskId: string,
    opts?: { completeIncompleteSubtasksFirst?: boolean },
  ) => Promise<void>;
  handleArchiveTask: (taskId: string) => Promise<void>;
  handleDeleteTask: (taskId: string) => void;
  confirmDeleteTask: (taskId: string) => Promise<void>;
  handleDeleteSubtask: (subtaskId: string) => void;
  confirmDeleteSubtask: (subtaskId: string) => Promise<void>;
  handleNavigateTask: (direction: "next" | "prev") => void;
  handleMoveSubtask: (subtaskId: string, targetTaskId: string) => Promise<void>;
  handleConvertSubtaskToTask: (subtaskId: string) => Promise<void>;
  handleConvertTaskToSubtask: (
    taskId: string,
    targetTaskId: string,
  ) => Promise<void>;
  handleSubtaskNavigate: (subtaskId: string | null) => void;
}

export function useTaskEditing({
  tasks,
  taskId,
  isLoading,
  navigate,
  pathname,
  dialog,
  viewBeforeTaskOpen,
  visibleTasks,
  clearSearchTerm,
  activeSubtaskId = null,
  navigationFocus,
  mutations,
}: UseTaskEditingArgs): UseTaskEditingResult {
  const {
    toggleTaskInDb,
    updateTaskInDb,
    updateSubtaskInDb,
    deleteTaskFromDb,
    archiveTaskInDb,
    addSubtaskToDb,
    deleteSubtaskFromDb,
    deleteSubtasksFromDb,
    moveSubtaskInDb,
    convertSubtaskToTaskInDb,
    convertTaskToSubtaskInDb,
    setTaskTags,
  } = mutations;

  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [originalEditingTask, setOriginalEditingTask] =
    useState<Task | null>(null);
  const { tagIds, setTagIds, sync: tagSync } = useEditingTaskTags();
  const [searchJumpedFromTaskId, setSearchJumpedFromTaskId] = useState<
    string | null
  >(null);
  const {
    navigationSnapshotIds,
    setNavigationSnapshotIds,
    navigationTasks,
    canNavigate,
    handleNavigateTask,
  } = useDialogCycle({
    taskId,
    tasks,
    visibleTasks,
    navigate,
    editingTask,
    activeSubtaskId,
    navigationFocus,
    blockNavigation:
      !!searchJumpedFromTaskId && taskId !== searchJumpedFromTaskId,
  });
  const [convertingTaskId, setConvertingTaskId] = useState<string | null>(null);
  const [pendingRecurringChange, setPendingRecurringChange] = useState<{
    task: RecurringChangeTask;
    resolve: (confirmed: boolean) => void;
  } | null>(null);

  // Mirrors of the two states, so the sync effect below can tell "the user has
  // typed something" from "still showing exactly what we loaded" without taking
  // either as a dependency — depending on them would re-run it on every
  // keystroke. Written in a layout effect so the mirror is current before the
  // sync effect reads it.
  const editingTaskRef = useRef<Task | null>(null);
  const originalEditingTaskRef = useRef<Task | null>(null);
  // Stamp of the task this dialog last saved. Only that task ignores an older copy.
  const savedBaselineRef = useRef<{ id: string; updatedAt: string } | null>(null);
  useLayoutEffect(() => {
    editingTaskRef.current = editingTask;
    originalEditingTaskRef.current = originalEditingTask;
  });

  // Re-adopt a server copy only while the user has not edited. A copy older
  // than a save this dialog already sent is the previous rule arriving late.
  useEffect(() => {
    if (!taskId || isLoading) return;
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    const current = editingTaskRef.current;
    const loaded = originalEditingTaskRef.current;
    const isSameTask = current?.id === taskId;
    // Tags sit outside the snapshot, so they are asked separately.
    const hasUnsavedEdits =
      isSameTask &&
      !!loaded &&
      (!sameTaskContent(current, loaded) || tagSync.hasPendingChange());
    if (hasUnsavedEdits) return;
    if (
      savedBaselineRef.current?.id === task.id &&
      isSameTask &&
      loaded &&
      !isNewerRevision(task.updated_at, savedBaselineRef.current.updatedAt)
    ) {
      return;
    }

    // Before the snapshot check below, not after: reopening a closed task hits
    // that early return, and the tags must come back with it.
    tagSync.adopt(task);
    const fresh = {
      ...task,
      subtasks: task.subtasks ? [...task.subtasks] : [],
    };
    // Nothing changed since the last adopt — skip so we don't re-render on
    // every unrelated task-list update.
    if (isSameTask && loaded && sameTaskContent(fresh, loaded)) return;

    setEditingTask(fresh);
    // Kept in lockstep with editingTask: the save path diffs the two to work
    // out which subtasks were added, removed or changed, so they must always
    // describe the same load.
    setOriginalEditingTask({
      ...task,
      subtasks: task.subtasks ? [...task.subtasks] : [],
    });
  }, [taskId, isLoading, tasks, tagSync]);

  // Wire the imperative confirmRecurringDateChange utility to dialog state.
  // Any caller (TaskDialog, drag handlers, etc.) can await the utility and
  // the same warning dialog opens, regardless of where in the tree the call
  // originates.
  useEffect(() => {
    registerRecurringChangeConfirmHandler((task) => {
      return new Promise<boolean>((resolve) => {
        setPendingRecurringChange({ task, resolve });
      });
    });
    return () => {
      registerRecurringChangeConfirmHandler(null);
    };
  }, []);

  const showEditDialog = !!taskId;
  const taskNotFound =
    !!taskId && !isLoading && !tasks.find((t) => t.id === taskId);

  const updateTask = async () => {
    const source = editingTask;
    if (!source || !source.title.trim()) return;
    savedBaselineRef.current = { id: source.id, updatedAt: source.updated_at };

    await updateTaskInDb(
      source.id,
      buildTaskUpdatePayload(source),
      // The TaskDialog's recurrence editor exposes the full rule and the
      // next-occurrence date, and every control in it is a direct edit of the
      // task the user has open. Any save reaching this path is already an
      // explicit user action, so suppress the re-anchor warning that's meant
      // to catch accidental drag-and-drop re-anchoring.
      { confirmedRecurringDateChange: true },
    );

    // Handle subtask changes.
    const originalSubtasks = originalEditingTask?.subtasks || [];
    const currentSubtasks = source.subtasks || [];

    // Find deleted subtasks (exist in original but not in current)
    const deletedSubtasks = originalSubtasks.filter(
      (original) =>
        !currentSubtasks.some((current) => current.id === original.id),
    );

    // Find new subtasks (exist in current but not in original)
    const newSubtasks = currentSubtasks.filter(
      (current) =>
        !originalSubtasks.some((original) => original.id === current.id),
    );

    // Find modified subtasks (exist in both but any field changed)
    const modifiedSubtasks = currentSubtasks.filter((current) => {
      const original = originalSubtasks.find((o) => o.id === current.id);
      if (!original) return false;
      return (
        original.completed !== current.completed ||
        original.title !== current.title ||
        original.description !== current.description ||
        original.priority !== current.priority ||
        original.scheduled_date !== current.scheduled_date ||
        original.scheduled_time !== current.scheduled_time ||
        original.deadline_date !== current.deadline_date ||
        original.duration !== current.duration ||
        original.persistent !== current.persistent ||
        original.working_hours_only !== current.working_hours_only
      );
    });

    // Delete removed subtasks in one batched write. Deleting them one by one
    // would invalidate queryKeys.tasks once per subtask, and that key refetches
    // the whole task graph — those refetches would land in the middle of the
    // adds and updates below, momentarily re-rendering the board without the
    // just-added subtasks.
    await deleteSubtasksFromDb(deletedSubtasks.map((subtask) => subtask.id));

    // Add new subtasks, collect real IDs, and confirm the save with a toast.
    const idMapping = await saveNewSubtasks(source.id, newSubtasks, addSubtaskToDb, navigate);

    // Update modified subtasks with all changed fields
    for (const subtask of modifiedSubtasks) {
      await updateSubtaskInDb(subtask.id, {
        completed: subtask.completed,
        title: subtask.title,
        description: subtask.description,
        priority: subtask.priority,
        scheduled_date: subtask.scheduled_date,
        scheduled_time: subtask.scheduled_time,
        deadline_date: subtask.deadline_date,
        duration: subtask.duration,
        persistent: subtask.persistent,
        working_hours_only: subtask.working_hours_only,
      });
    }

    // Persist subtask order positions. Order is not a calendar column, so the
    // database trigger does not enqueue a calendar job for this write.
    for (const subtask of currentSubtasks) {
      const index = currentSubtasks.indexOf(subtask);
      const isNew = !originalSubtasks.some((o) => o.id === subtask.id);
      if (!isNew) {
        await updateSubtaskInDb(subtask.id, {
          order_position: (index + 1) * 100,
        });
      }
    }

    // The baseline moves only once the write lands (see useEditingTaskTags).
    if (await setTaskTags(source.id, tagIds)) tagSync.markSaved(tagIds);

    // Replace temp subtask IDs with real DB IDs and sync original state
    const updatedSubtasks = source.subtasks?.map((st) => {
      const realId = idMapping.get(st.id);
      return realId ? { ...st, id: realId } : st;
    });
    const updatedTask: Task = { ...source, subtasks: updatedSubtasks };

    if (idMapping.size > 0) {
      setEditingTask(updatedTask);
    }
    setOriginalEditingTask(updatedTask);
  };

  const handleEditTask = (task: Task, subtaskId?: string) => {
    // Opening a fresh task from outside the dialog flow (board click, etc.)
    // resets any in-progress search side-trip. viewBeforeTaskOpen.current
    // becomes the current pathname so Escape returns there.
    setSearchJumpedFromTaskId(null);
    viewBeforeTaskOpen.current = pathname;
    // Render the dialog on top of /calendar or /emails, not the board. Open
    // `subtaskId` if given, and never a sub-task left open from another task.
    const fromView =
      pathname === "/calendar"
        ? "calendar"
        : pathname === "/emails"
          ? "emails"
          : undefined;
    navigate({
      to: "/task/$taskId",
      params: { taskId: task.id },
      search: ({ subtask: _, ...prev }: Record<string, unknown>) =>
        ({ ...prev, ...(fromView && { view: fromView }), ...(subtaskId && { subtask: subtaskId }) }),
    });
  };

  const handleCloseEditDialog = () => {
    tagSync.reset();
    // Side-trip return: if the current task was opened via a search jump,
    // Escape goes back to the original task (and clears the search filter
    // so the original task dialog re-renders in its original cycling
    // context). The next Escape from that original task closes normally.
    if (searchJumpedFromTaskId && taskId !== searchJumpedFromTaskId) {
      const returnTaskId = searchJumpedFromTaskId;
      setSearchJumpedFromTaskId(null);
      clearSearchTerm?.();
      setNavigationSnapshotIds([]);
      navigate({
        to: "/task/$taskId",
        params: { taskId: returnTaskId },
        search: (prev: Record<string, unknown>) => {
          const { subtask: _, ...rest } = prev;
          return rest;
        },
      });
      return;
    }
    setSearchJumpedFromTaskId(null);
    navigate({
      to: viewBeforeTaskOpen.current || "/",
      search: (prev: Record<string, unknown>) => {
        const { subtask: _s, view: _v, ...rest } = prev;
        return rest;
      },
    });
  };

  // Complete (or uncomplete) from inside the TaskDialog. Routes through the
  // global toggle so the undo toast appears, then closes the dialog when the
  // user just completed something — staying open on uncomplete matches the
  // list view, which only fires a toast on completion.
  const handleToggleCompleteFromDialog = async (
    taskId: string,
    opts?: { completeIncompleteSubtasksFirst?: boolean },
  ) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    const wasCompleted = task.completed;
    if (opts?.completeIncompleteSubtasksFirst) {
      const incomplete = task.subtasks?.filter((s) => !s.completed) ?? [];
      await Promise.all(
        incomplete.map((s) => updateSubtaskInDb(s.id, { completed: true })),
      );
    }
    await toggleTaskInDb(taskId);
    if (!wasCompleted) {
      handleCloseEditDialog();
    }
  };

  // Archive from inside the TaskDialog: flip the archived flag, close the
  // dialog, and (if we're archiving — not restoring) navigate back to the
  // main board so the user isn't left staring at a now-hidden task.
  const handleArchiveTask = async (taskId: string) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    const wasArchived = task.archived;
    await archiveTaskInDb(taskId);
    if (!wasArchived) {
      navigate({ to: "/", search: (prev: Record<string, unknown>) => prev });
    }
  };

  const handleDeleteTask = (taskId: string) => {
    const task = tasks.find((t) => t.id === taskId);
    if (task) {
      dialog.open("delete-confirm", {
        type: "task",
        name: task.title,
        id: taskId,
        taskCount: 0,
      });
    }
  };

  const confirmDeleteTask = async (taskId: string) => {
    await deleteTaskFromDb(taskId);
    dialog.close();
    navigate({ to: "/", search: (prev: Record<string, unknown>) => prev });
  };

  // Deleting the sub-task currently open in the dialog's breadcrumb sub-view.
  // Mirrors the task-delete flow (confirm dialog → DB delete) but targets the
  // sub-task instead of its parent, so the header trash icon always acts on the
  // item the user is actually looking at.
  const handleDeleteSubtask = (subtaskId: string) => {
    const subtask = tasks
      .flatMap((t) => t.subtasks ?? [])
      .find((st) => st.id === subtaskId);
    if (subtask) {
      dialog.open("delete-confirm", {
        type: "subtask",
        name: subtask.title,
        id: subtaskId,
        taskCount: 0,
      });
    }
  };

  const confirmDeleteSubtask = async (subtaskId: string) => {
    // Persist the delete and update the tasks query cache (board + other
    // consumers reflect it, and it survives a reload).
    await deleteSubtaskFromDb(subtaskId);
    // The editingTask snapshot doesn't re-sync from the tasks cache (that
    // effect is taskId-gated to preserve in-progress edits), so strip the
    // subtask from both the working copy and the save-diff baseline. Without
    // this the parent dialog stays open showing the just-deleted subtask.
    const stripSubtask = (prev: Task | null) =>
      prev
        ? {
            ...prev,
            subtasks: (prev.subtasks ?? []).filter((st) => st.id !== subtaskId),
          }
        : prev;
    setEditingTask(stripSubtask);
    setOriginalEditingTask(stripSubtask);
    dialog.close();
    // Drop the ?subtask param so the dialog falls back to the parent task view
    // (its sub-view target is now gone), leaving the parent task itself open.
    if (taskId) {
      navigate({
        to: "/task/$taskId",
        params: { taskId },
        search: (prev: Record<string, unknown>) => {
          const { subtask: _subtask, ...rest } = prev;
          return rest;
        },
      });
    }
  };

  const {
    handleMoveSubtask,
    handleConvertSubtaskToTask,
    handleConvertTaskToSubtask,
  } = useSubtaskConversionHandlers({
    editingTask,
    tasks,
    navigate,
    moveSubtaskInDb,
    convertSubtaskToTaskInDb,
    convertTaskToSubtaskInDb,
  });

  const handleSubtaskNavigate = (subtaskId: string | null) => {
    if (!taskId) return;
    if (subtaskId) {
      navigate({
        to: "/task/$taskId",
        params: { taskId },
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          subtask: subtaskId,
        }),
      });
    } else {
      navigate({
        to: "/task/$taskId",
        params: { taskId },
        search: (prev: Record<string, unknown>) => {
          const { subtask: _, ...rest } = prev;
          return rest;
        },
      });
    }
  };

  return {
    editingTask,
    setEditingTask,
    originalEditingTask,
    editingTaskTagIds: tagIds,
    setEditingTaskTagIds: setTagIds,
    searchJumpedFromTaskId,
    setSearchJumpedFromTaskId,
    navigationSnapshotIds,
    setNavigationSnapshotIds,
    navigationTasks,
    canNavigate,
    showEditDialog,
    taskNotFound,
    convertingTaskId,
    setConvertingTaskId,
    pendingRecurringChange,
    setPendingRecurringChange,
    updateTask,
    handleEditTask,
    handleCloseEditDialog,
    handleToggleCompleteFromDialog,
    handleArchiveTask,
    handleDeleteTask,
    confirmDeleteTask,
    handleDeleteSubtask,
    confirmDeleteSubtask,
    handleNavigateTask,
    handleMoveSubtask,
    handleConvertSubtaskToTask,
    handleConvertTaskToSubtask,
    handleSubtaskNavigate,
  };
}
